import { Paper } from '@mui/material'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import { fireReactGAEvent, questionFontStyle } from '../../Questions/utils'
import { ApplicationFormContext, RequiredDetailsValues } from '../ApplicationFormContext'
import QuestionKey from '../../../Questions/question-key'
import AppFormQuestion from '../../AppFormQuestion'
import withFade from '../../HOC/withFade'
import withQuestion from '../../HOC/withQuestion'
import QuestionComponentProps from '../../../Questions/question-component-props'
import {
  checkErrs,
  createBoolValues,
  createValidationErrors,
  createDebounceMap,
  FieldBools,
  FieldFnVoid,
  FieldKey
} from '../../../utils/RequiredDetailsPageApplicationForm-utils'

const RequiredDetailsFields = ({
  requiredFields = {}
}: QuestionComponentProps): JSX.Element => {
    const { dispatch } = useContext(ApplicationFormContext)

    const isJobTitleRequired = requiredFields.jobTitle != null
    const initialFieldBools = createBoolValues(requiredFields)
    
    const initialDirtyFields = Object.fromEntries(
      Object.entries(requiredFields).map(([key, value]) => [key, !!value])
    ) as FieldBools

    const [values, setValues] = useState<RequiredDetailsValues>(requiredFields)
    const [showErrors, setShowErrors] = useState<FieldBools>(initialFieldBools)
    const [dirtyFields, setDirtyFields] = useState<FieldBools>(initialDirtyFields)

    const validationErrors = createValidationErrors(requiredFields, values)
    const errors = Object.values(validationErrors).filter(Boolean) as string[]

    const debouncedSetShowError = (key: FieldKey) => {
        setShowErrors((previous) => ({ ...previous, [key]: true }))
            fireReactGAEvent({
            category: 'Application Form',
            action: 'Validation Error',
            label: key as string,
            nonInteraction: true
        })
    }

    const debounceMap: FieldFnVoid = useMemo(() => {
        return createDebounceMap(requiredFields, debouncedSetShowError)
    }, [requiredFields])

    const setShouldShowError = useCallback((
        key: FieldKey,
        errorMessage: string,
        isDirty: boolean,
        isAlreadyShowing: boolean
    ) => {
        if (!(isDirty && errorMessage)) {
            setShowErrors((previous) => ({ ...previous, [key]: false }))
            return
        }
        if (isAlreadyShowing) {
            setShowErrors((previous) => ({ ...previous, [key]: true }))
            return
        }

        debounceMap[key]?.()
    }, [debounceMap])

    const onChange = useCallback((key: FieldKey, value: string) => {
        if (!dirtyFields[key]) setDirtyFields((previous) => ({ ...previous, [key]: true }))
        setValues((previous) => ({ ...previous, [key]: value }))
    }, [dirtyFields])
  
    useEffect(() => {
        (Object.keys(requiredFields) as FieldKey[]).forEach((fieldKey) => {
            setShouldShowError(
                fieldKey,
                validationErrors[fieldKey] || '',
                !!dirtyFields[fieldKey],
                !!showErrors[fieldKey]
            )
        })
    }, [values])

    useEffect(() => { 
        const noErrs = checkErrs(errors)
        dispatch({
            type: 'SAVE',
            name: QuestionKey.REQUIRED_DETAILS,
            value: noErrs ? values : {},
            errors: showErrors
        })
    }, [showErrors])

  return (
    <Paper elevation={6} sx={{ p: 5, borderRadius: '12px' }}>
      <AppFormQuestion
        questionKey={QuestionKey.REQUIRED_DETAILS}
        mt={3}
        mb={3}
        textStyle={questionFontStyle}
      >
        Required Details
      </AppFormQuestion>

      <MuiTextField
        value={values.contactNumber || ''}
        aria-label="Mobile number"
        autoComplete="tel"
        data-hj-suppress=""
        errorMessage={showErrors.contactNumber ? validationErrors.contactNumber : undefined}
        id="contactNumber"
        inputMaxLength={20}
        inputType="tel"
        inputVariant="outlined"
        label="Mobile number"
        mb="34px"
        {...(isJobTitleRequired && { multipleInputs: true })}
        name="contactNumber"
        onChangeHandler={(event) => onChange('contactNumber', event.currentTarget.value)}
        testId="contactNumber"
      />

      {isJobTitleRequired && (
        <MuiTextField
          value={values.jobTitle || ''}
          aria-label="Job title"
          data-hj-suppress=""
          errorMessage={showErrors.jobTitle ? validationErrors.jobTitle : undefined}
          id="jobTitle"
          inputMaxLength={100}
          inputType="text"
          inputVariant="outlined"
          label="Job title"
          mb="34px"
          name="jobTitle"
          onChangeHandler={(event) => onChange('jobTitle', event.currentTarget.value)}
          testId="jobTitle"
        />
      )}
    </Paper>
  )
}

export default withFade(withQuestion(RequiredDetailsFields))